import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import each from 'lodash/each';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import size from 'lodash/size';
import { setWindowLocation } from '@eb/http';
import { HAS_WINDOW } from '@eb/feature-detection';

import { logDiscoveryRequest } from '../api/analytics';
import * as analytics from '@eb/site-analytics';
import { keysCamelToSnake } from '@eb/transformation-utils';
import {
    storeQuery,
    setLocationData,
    location as locationDuck,
} from '@eb/destination-utils';

import {
    search as searchApi,
    searchAutocompleteSuggestions as searchAutocompleteSuggestionsApi,
    searchSuggestionsForCity as searchSuggestionsForCityApi,
} from '../api/search';
import { getSearchEventHeaderTitle, updateDocumentTitle } from '../utils';
import { transformSearchBucketContent } from '../utils/transformation';
import {
    parseSuggestions,
    transformSearchIntoSuggestions,
} from '../utils/suggestions';
import { getPathWithSanitizedQueryParams } from '../utils/url';

import { updateContent } from './content';
// eslint-disable-next-line import/no-cycle
import { updateSearchLocation } from './location';
// eslint-disable-next-line import/no-cycle
import { setCurrentPage, resetPagination, getFuturePage } from './pagination';
import {
    CUSTOM_DATE,
    CLEAR_PRICE_FILTER,
    CURRENT_FUTURE,
    FORMAT_TAG_TYPE,
    CATEGORY_TAG_TYPE,
    GA_SEARCH_CATEGORY,
    GA_LOCATION_CHANGE_ACTION,
    EVENT_PAGE_PARAM,
    EVENTS_TYPE,
    ALL_EVENTS_TYPE,
    GA_REDO_FROM_MAP,
    GA_VIEW_EVENT_MAP_VIEW,
    LOCATION_STORAGE_KEY,
    SEARCH_EVENT_NAVIGATION,
} from '../constants';
import { getFilterEventTrackingData } from '../analytics';

const { trackEventFromState } = analytics;
const {
    actions: { updateLocation },
} = locationDuck;

export const UPDATE_SEARCH_CRITERIA = 'UPDATE_SEARCH_CRITERIA';
export const updateSearchCriteria = (searchQuery) => ({
    type: UPDATE_SEARCH_CRITERIA,
    payload: {
        eventSearch: searchQuery,
    },
});

export const removeBBoxFromSearchCriteria = (eventSearch) =>
    updateSearchCriteria(omit(eventSearch, ['bbox']));

export const countUnattendableEvents = (searchResponse) => {
    return searchResponse.events.results.reduce((prev, event) => {
        let isAttendable = true;

        if (event.event_sales_status.sales_status !== 'on_sale') {
            isAttendable = false;
        }

        // Series events should always be considered attendable
        if (event.num_children > 1) {
            isAttendable = true;
        }

        return isAttendable ? prev : prev + 1;
    }, 0);
};

/*
Helper which performs a search action with novel data, clearing
any existing pagination state. And updates route logic to represent
new search that was performed.
*/
const _doNewSearch = (
    history,
    searchQuery,
    gaTrackingData = [],
    shouldUpdateRoute = true,
) => (dispatch, getState) => {
    const state = getState();
    const {
        search: { eventSearch },
        app: { guestId, publicId },
        location: { currentPlace, currentPlaceParent, placeType, slug },
        admin: { experimentOverride } = {},
        user: { isStaff } = {},
    } = state;
    dispatch(resetPagination());

    // update page title with new search query data
    updateDocumentTitle(
        getSearchEventHeaderTitle(
            { currentPlace, currentPlaceParent, placeType },
            { eventSearch: searchQuery },
            true,
        ),
    );

    // search optimistic update
    dispatch(updateSearchCriteria(searchQuery));

    // location optimistic update
    const queryParamsToOmit = [EVENT_PAGE_PARAM];

    let searchTime = new Date().getTime();
    let searchResponse;

    return searchApi({ searchQuery, experimentOverride, isStaff, history })
        .then((response) => {
            searchResponse = response;

            return dispatch(
                updateContent(transformSearchBucketContent(response)),
            );
        })
        .then(() => dispatch(setCurrentPage(0)))
        .then(() => {
            let locationSlug = null;

            if (
                eventSearch.places &&
                searchQuery.places &&
                eventSearch.places[0] === searchQuery.places[0]
            ) {
                locationSlug = slug;
            }

            const searchId = logDiscoveryRequest({
                eventSearch: searchQuery,
                guestId,
                locationSlug,
                publicUserId: publicId,
                userGenerated: true,
                navigation: SEARCH_EVENT_NAVIGATION,
                action: gaTrackingData
                    .map((d) => d.action)
                    .sort()
                    .join(','),
                // variant can be used to track experiments
                variant: null,
            });

            const searchResponseTime = new Date().getTime() - searchTime;

            const searchResultCount = searchResponse
                ? searchResponse.events.results.length
                : 0;

            let metricsToTrack = {
                searchQueryResponseTime: searchResponseTime,
                searchResultCount,
            };

            // Don't track unattendable if we don't yet have the event sales status
            const shouldTrackUnattendableEventCount =
                searchResultCount &&
                searchResponse &&
                searchResponse.events.results[0].event_sales_status;

            if (shouldTrackUnattendableEventCount) {
                const unattendableEventCount = countUnattendableEvents(
                    searchResponse,
                );

                metricsToTrack = {
                    ...metricsToTrack,
                    searchResultCountUnattendable: unattendableEventCount,
                };
            }

            if (size(gaTrackingData)) {
                each(gaTrackingData, (gaData) => {
                    if (searchId) {
                        trackEventFromState(state, {
                            ...gaData,
                            dimensions: {
                                ...gaData.dimensions,
                                searchId,
                            },
                            metrics: metricsToTrack,
                        });
                    } else {
                        trackEventFromState(state, gaData);
                    }
                });
            }
        })
        .then(() => {
            if (shouldUpdateRoute) {
                dispatch(updateSearchLocation(history, { queryParamsToOmit }));
            }
        });
};

const _getAndSetCurrentPageFromMostRecentSearch = () => (
    dispatch,
    getState,
) => {
    const {
        search: {
            eventSearch: { page },
        },
    } = getState();

    return dispatch(setCurrentPage(page - 1));
};

/*
Similar to _doNewSearch but does so without updating or modifying
any of the route information. This is used for handling browser
back/forward cases so that our displayed content is accurate to the already
present URL
*/
const _doSearchWithoutRouteUpdate = (searchQuery) => (dispatch, getState) => {
    //Compare the eventSearch object in state with the search query
    //coming in here. If the only thing changing is pagination param
    //and we have associated entities don't run full search and
    //don't reset pagination etc. Otherwise, _do_ reset
    //the pagination objet in state.
    const {
        search: { eventSearch },
        pagination: { event },
        location: { currentPlace, currentPlaceParent, placeType },
    } = getState();

    const paginationKeys = ['continuation', 'page'];
    const paginationOnlyChange = isEqual(
        omit(eventSearch, paginationKeys),
        omit(searchQuery, paginationKeys),
    );
    const zeroBasedPage = searchQuery.page - 1;

    /* The user navigated with the browser between paginated pages so
        just show associated results if we have the data */
    if (paginationOnlyChange && event[zeroBasedPage]) {
        dispatch(setCurrentPage(zeroBasedPage));
        return dispatch(updateSearchCriteria(searchQuery));
    }

    /* If a new search was ran between these state, reset the pagination store */
    if (!paginationOnlyChange) {
        dispatch(resetPagination());
    }

    // update page title with new search query data
    updateDocumentTitle(
        getSearchEventHeaderTitle(
            { currentPlace, currentPlaceParent, placeType },
            { eventSearch: searchQuery },
            true,
        ),
    );

    dispatch(updateSearchCriteria(searchQuery));
    dispatch(getFuturePage());

    /* In the case that we don't already have the entities in the store, re-run the search */
    return searchApi({ searchQuery })
        .then((response) =>
            dispatch(updateContent(transformSearchBucketContent(response))),
        )
        .then(() => dispatch(_getAndSetCurrentPageFromMostRecentSearch()));
};

export const UPDATE_SEARCH_SUGGESTIONS = 'UPDATE_SEARCH_SUGGESTIONS';
export const SUGGESTIONS_LOADING = 'SUGGESTIONS_LOADING';
const updateSearchAutocompleteSuggestions = (suggestions) => ({
    type: UPDATE_SEARCH_SUGGESTIONS,
    payload: suggestions,
});

export const searchAutocompleteSuggestions = (query) => (
    dispatch,
    getState,
) => {
    if (!query) {
        return dispatch(updateSearchAutocompleteSuggestions({}));
    }

    const {
        location: { placeId, isOnline },
    } = getState();

    dispatch({ type: SUGGESTIONS_LOADING });

    return searchAutocompleteSuggestionsApi({
        placeId,
        onlineEventsOnly: isOnline,
        query,
    }).then((response) =>
        dispatch(
            updateSearchAutocompleteSuggestions(parseSuggestions(response)),
        ),
    );
};

export const searchSuggestionsForCity = () => (dispatch, getState) => {
    const {
        location: { placeId, isOnline },
    } = getState();

    dispatch({ type: SUGGESTIONS_LOADING });

    return searchSuggestionsForCityApi({
        placeId,
        onlineEventsOnly: isOnline,
    }).then((response) =>
        dispatch(
            updateSearchAutocompleteSuggestions(
                parseSuggestions(transformSearchIntoSuggestions(response)),
            ),
        ),
    );
};

export const updateSearchInformationFromUrl = (path, queryParams) => (
    dispatch,
    getState,
) => {
    const { locationAndSearchInformation } = getState();

    // Construct path using same util to set key
    const constructedPath = getPathWithSanitizedQueryParams(path, queryParams);

    // get search data from state that matches with the given path and perform a search
    const data = locationAndSearchInformation[constructedPath];

    if (data) {
        const {
            search: { eventSearch },
            location: locationFromState,
        } = data;

        dispatch(updateLocation(locationFromState));

        dispatch(_doSearchWithoutRouteUpdate(eventSearch));
    } else {
        // If we somehow get into a state where we
        // don't have the associated URL in our store of searches,
        // reload the page to hit and server and get appropriate data

        // Until the API supports url slugs in place of place-ids (or place-ids are
        // ingrained) it is impossible to resolve the place id from a location on the
        // client. So we must hit the server.
        dispatch(getFuturePage());
        setWindowLocation(constructedPath);
    }
};

export const searchInformationForPlace = (
    {
        slug,
        placeId,
        latitude,
        longitude,
        placeType,
        currentPlace,
        currentPlaceParent,
        isUsingCurrentLocation,
        isOnline = false,
        history,
    },
    query,
) => (dispatch, getState) => {
    dispatch(removeBBoxFromSearchCriteria(getState().search.eventSearch));

    const {
        search: { eventSearch },
        location: { currentPlace: oldPlace },
    } = getState();

    const label = `${oldPlace}--${currentPlace}`;

    const gaTrackingData = {
        label,
        category: GA_SEARCH_CATEGORY,
        action: GA_LOCATION_CHANGE_ACTION,
    };

    const locationData = {
        slug,
        placeId,
        latitude,
        longitude,
        placeType,
        currentPlace,
        currentPlaceParent,
        isUsingCurrentLocation,
        isOnline,
    };

    dispatch(updateLocation(locationData));

    // See https://docs.google.com/document/d/1Vg-kyxM34gw71vw_5dLcfzFRhzM3scQ3FKtYUlWXLSA/edit?usp=sharing
    setLocationData(keysCamelToSnake(locationData));

    /* When the user selects "online" from the Recent suggestions the placeId is null and the slug is online,
        but the isOnline is false. Because of that we should change it to reflect the correct filter */
    if (!placeId && slug === 'online') {
        locationData.isOnline = true;
    }

    if (HAS_WINDOW && !locationData.isOnline) {
        /*
         * Stores the current place data in the localStorage.
         * To store an object we have to replace the `,` with `;`
         * because we later reconstruct the data separating different
         * locations by `,` and the object itself should not be separated
         * until parsed as a JSON object.
         */
        storeQuery(
            JSON.stringify({
                currentPlace,
                currentPlaceParent,
                content: currentPlace,
                secondaryContent: currentPlaceParent,
                value: `recent-${placeId}`,
                latitude,
                longitude,
                placeType,
                placeId,
                slug,
                isOnline,
                isHistorySuggestion: true,
            }).replace(/,/g, ';'),
            LOCATION_STORAGE_KEY,
        );
    }

    dispatch(searchSuggestionsForCity());

    const searchQuery = {
        ...eventSearch,
        q: !(!query && query !== '') ? query : eventSearch.q,
        page: 1,
        places: placeId ? [placeId] : [],
        onlineEventsOnly: isOnline,
    };

    dispatch(_doNewSearch(history, searchQuery, [gaTrackingData]));
};

export const cleanLongitude = (longitude) =>
    (((longitude % 360) + 540) % 360) - 180;

export const searchByBBox = (history, bbox) => (dispatch, getState) => {
    const state = getState();
    const {
        search: { eventSearch },
        location: { slug },
    } = state;

    // Create bounding box string of shape: '${left},${bottom},${right},${top}'
    const bboxParam = `${cleanLongitude(bbox.sw.lng)},${
        bbox.sw.lat
    },${cleanLongitude(bbox.ne.lng)},${bbox.ne.lat}`;

    const searchQuery = {
        ...eventSearch,
        onlineEventsOnly: false,
        bbox: bboxParam,
    };

    const gaTrackingData = {
        label: slug,
        category: GA_SEARCH_CATEGORY,
        action: GA_REDO_FROM_MAP,
    };

    dispatch(
        _doNewSearch(history, omit(searchQuery, 'places'), [gaTrackingData]),
    );
};

// If a bounding box is available, use it.  If not, search by place
export const searchByBBoxOrPlace = (history, place) => (dispatch) => {
    if ('bbox' in place) {
        dispatch(searchByBBox(history, place.bbox));
    } else {
        dispatch(searchInformationForPlace({ ...place, history }));
    }
};

export const viewEventMapView = (eventId) => (dispatch, getState) => {
    const state = getState();

    trackEventFromState(state, {
        label: eventId,
        category: GA_SEARCH_CATEGORY,
        action: GA_VIEW_EVENT_MAP_VIEW,
    });
};

const _isTagType = (tag, type) => tag.indexOf(type) > -1;

const _getUpdatedTags = (currentTags, tag, format) => {
    //Return current tags if no tag or format passed
    if (tag === undefined && format === undefined) {
        return currentTags;
    }

    let updatedTags = [...currentTags];

    if (tag || tag === '') {
        //updated tags should equal the new array minus the previously selected category
        updatedTags = [
            ...filter(
                updatedTags,
                (val) => !_isTagType(val, CATEGORY_TAG_TYPE),
            ),
            tag,
        ];
    }

    if (format || format === '') {
        updatedTags = [
            ...filter(updatedTags, (val) => !_isTagType(val, FORMAT_TAG_TYPE)),
            format,
        ];
    }

    return compact(updatedTags);
};

const _getUpdatedTagsForRemoval = (currentTags, tag, format) =>
    // This approach assues there will only ever be at most one of each type
    // If that is to change than the logic here will need to be updated
    compact(
        currentTags.map((currentTag) => {
            if (
                (tag && _isTagType(currentTag, CATEGORY_TAG_TYPE)) ||
                (format && _isTagType(currentTag, FORMAT_TAG_TYPE))
            ) {
                return '';
            }

            return currentTag;
        }),
    );

const _handlePriceUpdate = (price, priceInState) => {
    let priceValue = priceInState;

    if (price) {
        priceValue = price === CLEAR_PRICE_FILTER ? '' : price;
    }

    return priceValue;
};

const _getUpdatedDates = (dates, datesInState) => {
    if (dates === CUSTOM_DATE) {
        return CURRENT_FUTURE;
    } else if (dates) {
        return [CURRENT_FUTURE, dates];
    }
    return datesInState;
};

const _getUpdatedDateRange = (dates, startDate, endDate, dateRangeInState) => {
    if (startDate && endDate) {
        return { from: startDate, to: endDate };
    }
    if (dates) {
        return {};
    }
    return dateRangeInState;
};

const _getUpdatedArrayFilter = (filter, filterInState) => {
    if (filter) {
        if (typeof filter === 'string') {
            return [filter];
        } else {
            return filter;
        }
    }

    return filterInState;
};

const _getSpecialParams = (filterInState, filter) => {
    if (!filter) {
        return filterInState;
    }

    return uniq([...filterInState, filter]);
};

export const handleApplyFilter = (
    history,
    { tags, price, dates, q, format, languages, currencies, special, hashA },
    { startDate, endDate } = {},
    shouldUpdateRoute = true,
) => (dispatch, getState) => {
    const {
        search: { eventSearch },
    } = getState();

    const gaTrackingData = getFilterEventTrackingData(
        { tags, price, dates, q, format, languages, currencies, special },
        { startDate, endDate },
    );

    const updateSpecialParams = _getSpecialParams(eventSearch.special, special);
    const updatedTags = _getUpdatedTags(eventSearch.tags, tags, format);
    const updatedPrice = _handlePriceUpdate(price, eventSearch.price);
    const updatedDates = _getUpdatedDates(dates, eventSearch.dates);
    const updatedCurrencies = _getUpdatedArrayFilter(
        currencies,
        eventSearch.currencies,
    );
    const updatedLanguages = languages || eventSearch.languages;
    const updatedDateRange = _getUpdatedDateRange(
        dates,
        startDate,
        endDate,
        eventSearch.dateRange,
    );
    const updatedHashA = hashA || eventSearch.hashA;

    let searchQuery = {
        ...eventSearch,
        q: !(!q && q !== '') ? q : eventSearch.q,
        price: updatedPrice,
        tags: updatedTags,
        dates: updatedDates,
        dateRange: updatedDateRange,
        currencies: updatedCurrencies,
        languages: updatedLanguages,
        special: updateSpecialParams,
        hashA: updatedHashA,
        page: 1,
    };

    // when searching for 'events' or 'all events' filters should be resetted
    if (
        includes(
            [EVENTS_TYPE, ALL_EVENTS_TYPE],
            (searchQuery.q || '').toLowerCase(),
        )
    ) {
        searchQuery = {
            ...eventSearch,
            q: '',
            price: '',
            tags: '',
            dates: CURRENT_FUTURE,
            dateRange: '',
            page: 1,
            currencies: '',
            languages: '',
        };
    }

    dispatch(
        _doNewSearch(history, searchQuery, gaTrackingData, shouldUpdateRoute),
    );
};

export const handleRemoveFilter = (
    history,
    {
        tags,
        price,
        dates,
        format,
        currencies,
        languages,
        special,
        organizationsOr,
        hashA,
    },
) => (dispatch, getState) => {
    const {
        search: { eventSearch },
    } = getState();

    const updatedTags = _getUpdatedTagsForRemoval(
        eventSearch.tags,
        tags,
        format,
    );

    const searchQuery = {
        ...eventSearch,
        tags: updatedTags,
        price: price ? '' : eventSearch.price,
        dates: dates ? CURRENT_FUTURE : eventSearch.dates,
        dateRange: dates ? {} : eventSearch.dateRange,
        currencies: currencies ? '' : eventSearch.currencies,
        languages: languages ? '' : eventSearch.languages,
        organizationsOr: organizationsOr ? [] : eventSearch.organizationsOr,
        hashA: hashA ? '' : eventSearch.hashA,
        special: special ? [] : eventSearch.special,
        page: 1,
    };

    dispatch(_doNewSearch(history, searchQuery));
};

export const handleRemoveAllFilters = (history) => (dispatch) => {
    // Since we want to remove only the filters in the filter panel,
    // we don't want to spread ...eventSearch since that would reset
    // the q as well
    const searchQuery = {
        tags: [],
        price: '',
        dates: CURRENT_FUTURE,
        dateRange: {},
        currencies: '',
        languages: '',
        page: 1,
    };

    dispatch(_doNewSearch(history, searchQuery));
};
